import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { Lock, FlaskConical, ShieldAlert, LineChart, Globe2 } from 'lucide-react';
import RequestAccessForm from './RequestAccessForm';

const METRICS = [
  { k: 'Consented patients', v: '0', sub: 'Synthetic sandbox · no real patients' },
  { k: 'Languages', v: '22+', sub: 'Sarvam AI' },
  { k: 'Per recruited patient', v: '$5K–25K' },
  { k: 'Acquisition cost', v: '$0' },
];

function Metric({ k, v, sub }) {
  return (
    <div className="bg-white border border-amber-200 rounded-xl p-4" data-testid={`pharma-metric-${k.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
      <div className="text-2xl font-bold tracking-tight" style={{ color: '#B45309', fontFamily: "'Work Sans',sans-serif" }}>{v}</div>
      <div className="text-xs text-slate-500 uppercase tracking-wide mt-0.5">{k}</div>
      {sub && <div className="text-[11px] text-amber-600 mt-0.5">{sub}</div>}
    </div>
  );
}

function Acc({ value, icon: Icon, title, badge, badgeTone, children }) {
  return (
    <AccordionItem value={value} className="bg-white border border-amber-200 rounded-lg mb-3 overflow-hidden">
      <AccordionTrigger data-testid={`pharma-acc-${value}`} className="px-5 py-4 hover:bg-amber-50 hover:no-underline text-base font-semibold text-slate-800">
        <span className="flex items-center gap-2 text-left">
          <Icon className="w-4 h-4 text-amber-600 flex-shrink-0" />
          {title}
          {badge && (
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${badgeTone || 'bg-slate-100 text-slate-500'}`}>{badge}</span>
          )}
        </span>
      </AccordionTrigger>
      <AccordionContent className="px-5 pb-5 pt-1 text-sm text-slate-600 border-t border-amber-100 leading-relaxed">
        {children}
      </AccordionContent>
    </AccordionItem>
  );
}

export default function PharmaSurface() {
  return (
    <section className="physician-output max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="pharma-surface">
      <div className="border-2 border-amber-400 bg-amber-50 rounded-xl p-4 mb-6 flex items-center gap-3" data-testid="pharma-paywall">
        <Lock className="w-5 h-5 text-amber-600 flex-shrink-0" />
        <p className="text-sm font-semibold text-amber-900">Health Mart paywall · Enterprise licensing · Not visible to patients or physicians.</p>
      </div>

      <RequestAccessForm surface="pharma" />

      <h2 className="text-lg font-bold text-slate-900 mb-3" style={{ fontFamily: "'Work Sans',sans-serif" }}>Pharma AI</h2>
      <div className="grid grid-cols-2 gap-3 mb-8">
        {METRICS.map((m) => <Metric key={m.k} {...m} />)}
      </div>

      <Accordion type="multiple" className="w-full">
        <Acc value="recruitment" icon={FlaskConical} title="Clinical trial recruitment · 21st Century Cures Act">
          <p>Patient pulls own EHR under federal law — no hospital permission required. Deterministic eligibility matching. Multilingual consent. Diverse enrollment. No competitor has a patient-owned data source.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {['T2DM: 34 patients', 'Psoriasis/derm: 12', 'Cardiology: 28', 'Neurology: 9'].map((t) => (
              <span key={t} className="text-xs px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-200 font-medium">{t}</span>
            ))}
          </div>
        </Acc>

        <Acc value="pharmacovigilance" icon={ShieldAlert} title="Pharmacovigilance · adverse event detection (Moat F)" badge="In build" badgeTone="bg-slate-200 text-slate-600">
          <p>Deterministic adverse event flags from patient-reported symptoms vs FDA FAERS. Auto-generates MedWatch structure for physician review. FDA non-reporting fine avoided: <strong>$10M–$500M</strong>.</p>
        </Acc>

        <Acc value="rwe" icon={LineChart} title="Real-world evidence · pre/post clinical studies">
          <p>De-identified, consented, longitudinal outcome data. Multilingual. Pre/post study support. Enterprise licensing: <strong>$50K–$500K per study</strong>. 30% of clinical trial cost is patient recruitment — we eliminate it.</p>
        </Acc>

        <Acc value="equity" icon={Globe2} title="India / global health equity · Sarvam AI (Khosla Ventures portfolio)">
          <p>22 Indic languages in production via Sarvam AI. WHO IMCI pediatric engines. Ayurveda/Siddha cross-system pharmacology. SRMC Chennai pilot. Underrepresented in every competitor dataset.</p>
          <p className="mt-2 text-xs italic text-slate-500">Note: Khosla Ventures invested in Sarvam AI — also in our production stack.</p>
        </Acc>
      </Accordion>
    </section>
  );
}
